import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  getAuth, onAuthStateChanged, signInWithPopup, signInWithRedirect, getRedirectResult,
  GoogleAuthProvider, signOut, getAdditionalUserInfo,
  sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink, linkWithPopup, linkWithRedirect,
} from 'firebase/auth';
import { track } from '../services/analytics';
import { doc, onSnapshot } from 'firebase/firestore';
import { app, db } from '../services/firebase';
import { syncSubscription } from '../services/stripeService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [claims, setClaims] = useState(null); // custom claims from ID token
  const [subscription, setSubscription] = useState(null); // Firestore users/{uid} data (bonus)
  const [profile, setProfile] = useState(null); // Firestore profiles/{uid} (public creator/business profile)
  // The uid `profile` currently reflects — NOT a separate loading flag set from
  // inside an effect. A consumer effect (e.g. App.jsx's business-signup-intent
  // check) can run in the SAME commit as `user` changing, before this
  // component's own profile-listener effect has had a chance to flip a
  // separate "loading" state — child effects run before parent effects, so a
  // plain useState(true)/setProfileLoading(false) pattern here briefly reads
  // stale (loading=false, profile=null) right after sign-in and wrongly looks
  // "done, definitely no profile" for one render. Deriving profileLoading
  // during render instead — by comparing `user.uid` to the uid `profile` was
  // last fetched for — has no such gap: it's correct in the very same render
  // `user` changes, not a render behind.
  const [profileForUid, setProfileForUid] = useState(null);
  const profileLoading = !!user && profileForUid !== user.uid;
  const [loading, setLoading] = useState(!!app);
  const [authError, setAuthError] = useState(null);

  // Auth state listener — also reads initial custom claims
  useEffect(() => {

    if (!app) return;
    const auth = getAuth(app);
    const unsub = onAuthStateChanged(auth, async u => {
      setUser(u);
      if (!u) {
        setClaims(null);
        setSubscription(null);
      } else {
        const result = await u.getIdTokenResult();
        setClaims(result.claims);
        // If no subscription claims yet, auto-sync from Stripe in background
        if (!result.claims?.subscriptionStatus) {
          syncSubscription()
            .then(() => u.getIdTokenResult(true).then(r => setClaims(r.claims)))
            .catch(() => { }); // silent — user may have no subscription
        }
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // Firestore subscription listener — fires whenever users/{uid} changes (optional bonus)
  useEffect(() => {
    if (!user || !db) return;
    const ref = doc(db, 'users', user.uid);
    const unsub = onSnapshot(ref, snap => {
      setSubscription(snap.exists() ? snap.data() : null);
    }, () => { }); // silent error if Firestore API is disabled
    return unsub;
  }, [user]);

  // Public creator/business profile (profiles/{uid}) — live, so an admin approval
  // (verifiedStatus flipping to 'verified') shows up immediately without a
  // refresh. This is the single source of truth for gating business-only
  // capabilities (create tour/lesson, connect Stripe, Dashboard access).
  useEffect(() => {
    if (!user || !db) { setProfile(null); setProfileForUid(null); return; }
    const ref = doc(db, 'profiles', user.uid);
    const unsub = onSnapshot(ref, snap => {
      setProfile(snap.exists() ? { uid: user.uid, ...snap.data() } : null);
      setProfileForUid(user.uid);
    }, () => setProfileForUid(user.uid));
    return unsub;
  }, [user]);

  // Sync from Stripe then force-refresh token — call after checkout or manually
  const refreshSubscription = useCallback(async () => {
    if (!user) return;
    await syncSubscription().catch(() => { });
    const result = await user.getIdTokenResult(true);
    setClaims(result.claims);
  }, [user]);

  // Codes meaning the popup itself never got a chance to work — a browser/
  // extension popup blocker, or an in-app browser (Instagram/Facebook/TikTok
  // webviews) that doesn't support popups at all. The fix isn't a better
  // error message, it's a different flow: signInWithRedirect navigates the
  // whole page to Google instead of opening a popup, so it can't be blocked
  // the same way. Completion is picked up by the getRedirectResult effect below.
  const POPUP_BLOCKED_CODES = new Set([
    'auth/popup-blocked',
    'auth/operation-not-supported-in-this-environment',
  ]);

  const signIn = useCallback(async () => {
    if (!app) return;
    setAuthError(null);
    const auth = getAuth(app);
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      // Only count genuinely new accounts as sign-ups (not returning logins).
      if (getAdditionalUserInfo(result)?.isNewUser) {
        track('sign_up', { method: 'google' });
      }
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return;
      if (POPUP_BLOCKED_CODES.has(err.code)) {
        // Navigates away — nothing more happens in this function; the app
        // reloads and getRedirectResult() below picks up the outcome.
        await signInWithRedirect(auth, new GoogleAuthProvider()).catch(() => { });
        return;
      }
      const msg = err.code === 'auth/configuration-not-found'
        ? 'Sign-in not configured — check Firebase Auth authorized domains.'
        : (err.message ?? 'Sign-in failed.');
      setAuthError(msg);
      console.error('Auth error:', err.code, err.message);
    }
  }, []);

  // Picks up the result when signIn() had to fall back to signInWithRedirect
  // (see POPUP_BLOCKED_CODES above) — the page navigated away and back, so
  // there's no in-memory promise to await; Firebase hands the outcome back
  // via this call instead. A no-op (resolves to null) on every normal load
  // that didn't just return from a redirect.
  useEffect(() => {
    if (!app) return;
    getRedirectResult(getAuth(app))
      .then(result => {
        if (result && getAdditionalUserInfo(result)?.isNewUser) {
          track('sign_up', { method: 'google' });
        }
      })
      .catch(err => {
        console.error('Redirect sign-in failed:', err.code, err.message);
        setAuthError(err.message ?? 'Sign-in failed.');
      });
  }, []);

  // Attach Google as an additional sign-in method for the current account (e.g.
  // an email-link user who's tired of clicking a link every time). The Google
  // account doesn't need to share the same email domain — Google accounts can
  // be registered under any address. If that Google identity is already its
  // own separate Firebase account, Firebase refuses to merge them; we surface
  // that as a clear message rather than a raw error code.
  const linkGoogle = useCallback(async () => {
    if (!app || !user) return;
    try {
      await linkWithPopup(user, new GoogleAuthProvider());
      track('link_provider', { method: 'google' });
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') return;
      if (POPUP_BLOCKED_CODES.has(err.code)) {
        // Same fallback as signIn() above — the redirect result is picked up
        // by the getRedirectResult effect there too (linking or fresh
        // sign-in both resolve through the same call).
        await linkWithRedirect(user, new GoogleAuthProvider()).catch(() => { });
        return;
      }
      const msg = err.code === 'auth/credential-already-in-use'
        ? 'That Google account is already linked to a different Motoroam account — sign in with Google directly instead of linking.'
        : (err.message ?? 'Could not link Google account.');
      throw new Error(msg);
    }
  }, [user]);

  const signOutUser = useCallback(async () => {
    if (!app) return;
    await signOut(getAuth(app));
  }, []);

  // ── Passwordless email-link sign-in ─────────────────────────────────────────
  const EMAIL_KEY = 'motoroam_signin_email';

  const sendEmailLink = useCallback(async (email) => {
    if (!app) return;
    setAuthError(null);
    const auth = getAuth(app);
    await sendSignInLinkToEmail(auth, email, {
      url: window.location.origin,   // return to the app; completion handled below
      handleCodeInApp: true,
    });
    // Remembered so the same-device click-through completes without re-typing.
    try { localStorage.setItem(EMAIL_KEY, email); } catch { /* private mode */ }
  }, []);

  // Complete the flow when the user lands back on the app from the email link.
  useEffect(() => {
    if (!app) return;
    const auth = getAuth(app);
    if (!isSignInWithEmailLink(auth, window.location.href)) return;

    let email = null;
    try { email = localStorage.getItem(EMAIL_KEY); } catch { /* ignore */ }
    // Cross-device: the link was opened somewhere the email isn't stored.
    if (!email) email = window.prompt('Confirm your email to finish signing in:');
    if (!email) return;

    signInWithEmailLink(auth, email, window.location.href)
      .then(result => {
        try { localStorage.removeItem(EMAIL_KEY); } catch { /* ignore */ }
        // Strip the auth params (?apiKey=…&oobCode=…) from the URL.
        window.history.replaceState(null, '', window.location.pathname);
        if (getAdditionalUserInfo(result)?.isNewUser) {
          track('sign_up', { method: 'email_link' });
        }
      })
      .catch(err => {
        console.error('Email-link sign-in failed:', err.code, err.message);
        setAuthError(
          err.code === 'auth/invalid-action-code'
            ? 'That sign-in link has expired or was already used. Request a new one.'
            : (err.message ?? 'Sign-in failed.'),
        );
        window.history.replaceState(null, '', window.location.pathname);
      });
  }, []);

  // Claims are authoritative; Firestore is a real-time bonus if API is enabled
  const subStatus = claims?.subscriptionStatus || subscription?.subscriptionStatus;
  const isSubscribed = subStatus === 'active' || subStatus === 'trialing';

  return (
    <AuthContext.Provider value={{
      user,
      claims,
      subscription,
      profile,
      profileLoading,
      isSubscribed,
      loading,
      authError,
      signIn,
      sendEmailLink,
      linkGoogle,
      signOut: signOutUser,
      refreshSubscription,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
